/**
 * Frontend Environment Configuration
 *
 * This file provides centralized configuration for all environment-dependent URLs.
 * In production, these values should be set via environment variables during build.
 *
 * Usage:
 *   import { config } from './config/env';
 *   const apiUrl = config.API_BASE_URL;
 */

// Helper function to detect if we're running in production
const isProduction = () => {
  return import.meta.env.MODE === 'production';
};

// Helper function to get the current origin
const getCurrentOrigin = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
};

// Runtime config injected per-environment by the backend at /config.js
// (window.__APP_CONFIG__). It takes precedence over the build-time-baked
// VITE_* values so a single web image is promotable across staging↔prod:
// whichever environment serves the bundle also serves its own URLs here.
// Returns undefined when unset, so the VITE_*/hardcoded fallbacks still apply.
const runtime = (key) => {
  if (typeof window !== 'undefined' && window.__APP_CONFIG__) {
    const v = window.__APP_CONFIG__[key];
    if (v) return v;
  }
  return undefined;
};

// The COMMON_AUTH_* URLs are REQUIRED and come from the backend's /config.js
// (window.__APP_CONFIG__) or a build-time VITE_* arg. There is deliberately NO
// production placeholder: a missing value means a misconfiguration (e.g. an old
// image without /config.js), so we FAIL LOUD — console.error + a visible banner —
// and return '' rather than silently redirecting users to a dead domain.
let _authConfigErrorShown = false;
const reportMissingAuthConfig = (key) => {
  console.error(
    `[config] ${key} is not configured — backend /config.js (window.__APP_CONFIG__) ` +
    `is missing/empty and no VITE_${key} was baked into the build. Authentication is broken; ` +
    `check the deployed image + the backend's /config.js.`
  );
  if (typeof document === 'undefined' || _authConfigErrorShown) return;
  _authConfigErrorShown = true;
  const show = () => {
    if (document.getElementById('__auth_config_error__')) return;
    const el = document.createElement('div');
    el.id = '__auth_config_error__';
    el.setAttribute('role', 'alert');
    el.style.cssText =
      'position:fixed;top:0;left:0;right:0;z-index:2147483647;background:#b00020;color:#fff;' +
      'padding:10px 16px;font:600 14px/1.4 system-ui,sans-serif;text-align:center';
    el.textContent =
      'Configuration error: the authentication service URL is not set (missing /config.js). ' +
      'Login is unavailable — check the deployment.';
    (document.body || document.documentElement).appendChild(el);
  };
  if (document.body) show();
  else if (typeof window !== 'undefined') window.addEventListener('DOMContentLoaded', show);
};

// Resolve a required auth URL: runtime (/config.js) > build-time VITE_* > (dev only)
// localhost. Production has NO placeholder fallback — a missing value fails loud.
const resolveAuthUrl = (key, viteVal, path) => {
  const v = runtime(key) || viteVal;
  if (v) return v;
  if (!isProduction()) return `http://localhost:8000${path}`;
  reportMissingAuthConfig(key);
  return '';
};

// Environment configuration with fallbacks
export const config = {
  // Environment mode
  MODE: import.meta.env.MODE || 'development',

  // Backend API URLs — precedence: runtime (/config.js) > build-time VITE_* >
  // hardcoded fallback. In production they default to the current origin
  // (backend serving frontend); in development they point to the separate
  // backend server.
  BACKEND_URL: runtime('BACKEND_URL') || import.meta.env.VITE_BACKEND_URL ||
    (isProduction() ? getCurrentOrigin() : 'http://localhost:8001'),

  API_BASE_URL: import.meta.env.VITE_API_BASE_URL || '/api/v1',

  // WebSocket URL (ws:// in dev, wss:// in production)
  WS_BASE_URL: runtime('WS_BASE_URL') || import.meta.env.VITE_WS_BASE_URL ||
    (isProduction()
      ? `wss://${typeof window !== 'undefined' ? window.location.host : ''}`
      : 'ws://localhost:8001'),

  // No production placeholder — missing config fails loud (see resolveAuthUrl).
  COMMON_AUTH_URL: resolveAuthUrl('COMMON_AUTH_URL', import.meta.env.VITE_COMMON_AUTH_URL, ''),

  COMMON_AUTH_LOGIN_URL: resolveAuthUrl('COMMON_AUTH_LOGIN_URL', import.meta.env.VITE_COMMON_AUTH_LOGIN_URL, '/login'),

  COMMON_AUTH_DASHBOARD_URL: resolveAuthUrl('COMMON_AUTH_DASHBOARD_URL', import.meta.env.VITE_COMMON_AUTH_DASHBOARD_URL, '/dashboard'),
  
  // Feature flags (can be overridden via env vars)
  ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
  ENABLE_DEBUG_LOGS: true, // Force debug logs to be enabled in all environments for debugging
};

// Helper functions
export const getApiUrl = (endpoint) => {
  // If endpoint starts with /, use it directly (relative to API_BASE_URL)
  if (endpoint.startsWith('/')) {
    return `${config.API_BASE_URL}${endpoint}`;
  }
  // Otherwise, append to API_BASE_URL with a slash
  return `${config.API_BASE_URL}/${endpoint}`;
};

export const getWebSocketUrl = (endpoint) => {
  const baseUrl = config.WS_BASE_URL;
  const path = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${baseUrl}/${path}`;
};

export const getCommonAuthUrl = (path = '') => {
  return `${config.COMMON_AUTH_URL}${path}`;
};

// Log configuration in development
if (config.ENABLE_DEBUG_LOGS) {
  console.log('🔧 Environment Configuration:', {
    MODE: config.MODE,
    BACKEND_URL: config.BACKEND_URL,
    API_BASE_URL: config.API_BASE_URL,
    WS_BASE_URL: config.WS_BASE_URL,
    COMMON_AUTH_URL: config.COMMON_AUTH_URL,
    COMMON_AUTH_LOGIN_URL: config.COMMON_AUTH_LOGIN_URL,
  });
}

export default config;
